import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import InvoiceCard from "./InvoiceCard";
import DateRangeFilter from "../utilComponent/DateRangeFilter";

const Invoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoiceList, setInvoiceList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState({});
  const [date, setDate] = useState();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
  });

  const handleDateRangeChange = ({ fromDate, toDate }) => {
    setDate("");
    setFromDate(fromDate);
    setToDate(toDate);
    setPagination((p) => ({ ...p, current_page: 1 }));
  };

  const getInvoiceList = async (
    page = pagination.current_page,
    perPage = pagination.per_page,
  ) => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append("page", page);
      params.append("per_page", perPage);

      if (date) {
        params.append("fromdate", date);
        params.append("todate", date);
      } else if (fromDate && toDate) {
        params.append("fromdate", fromDate);
        params.append("todate", toDate);
      } else if (fromDate) {
        params.append("fromdate", fromDate);
      } else if (toDate) {
        params.append("todate", toDate);
      }

      const res = await request({
        url: `invoice-details?${params.toString()}&user_id=${id}`,
        method: "GET",
      });

      if (res.status) {
        setInvoiceList(res.data.items || []);
        setTotal(res.data.totals || {});

        setPagination((prev) => ({
          ...prev,
          current_page: page,
          per_page: res.data.pagination?.per_page || perPage,
          total: res.data.pagination?.total || 0,
        }));
      }
    } catch (error) {
      toast.error("Failed to load invoice data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInvoiceList(1);
  }, [date, fromDate, toDate]);

  const handlePageChange = (page, perPage = pagination.per_page) => {
    const nextPage = perPage !== pagination.per_page ? 1 : page;

    setPagination((prev) => ({
      ...prev,
      current_page: nextPage,
      per_page: perPage,
    }));

    getInvoiceList(nextPage, perPage);
  };

  const filteredInvoiceList = useMemo(() => {
    return invoiceList.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    );
  }, [invoiceList, searchText]);

  const getItemPayment = (item) => {
    const apiPayment = Number(item?.payment);
    const hasApiPayment = Number.isFinite(apiPayment) && apiPayment > 0;
    if (hasApiPayment) return apiPayment;

    const hasProduct = !!item?.product;
    const price = hasProduct ? Number(item?.price ?? item?.product?.final_price ?? 0) : 0;
    const qty = Number(item?.product_qty || 0);
    return hasProduct ? price * qty : 0;
  };

  const getItemBalance = (item) => {
    const hasProduct = !!(item?.product || item?.product_id);
    const price = hasProduct ? Number(item?.price ?? item?.product?.final_price ?? 0) : 0;
    const qty = Number(item?.product_qty || 0);
    const deposit = Number(item?.deposit ?? 0);
    const amountNeedToPay = hasProduct ? price * qty : deposit;

    return amountNeedToPay - getItemPayment(item);
  };

  const totalQty = filteredInvoiceList.reduce(
    (s, i) => s + Number(i.product_qty || 0),
    0,
  );
  const totalDeposit = filteredInvoiceList.reduce(
    (s, i) => s + Number(i.deposit ?? 0),
    0,
  );
  const totalPayment = filteredInvoiceList.reduce(
    (s, i) => s + getItemPayment(i),
    0,
  );
  const totalBalance = totalDeposit - totalPayment;

  // const reloadInvoices = () => {
  //   getInvoiceList(pagination.current_page);
  // };

  return (
    <>
      <ToastContainer />
      <div className="container-fluid">
        <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">Calculation</li>
            <li className="breadcrumb-item active">Create Calculation</li>
          </ol>
        </div>
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h6 className="m-0">Calculation List</h6>
          </div>
          <div className="row px-4 my-3 g-3">
            <DateRangeFilter onChange={handleDateRangeChange} />
          </div>
          <div className="card-body">
            <InvoiceCard
              data={invoiceList}
              pagination={pagination}
              loading={loading}
              id={id}
              onBack={() => navigate("/calculation-view")}
              onPageChange={handlePageChange}
              onReload={() => getInvoiceList(pagination.current_page)}
              onRowUpdate={(rowId, key, value, fullRow, manualEdit = false) => {
                setInvoiceList((prev) =>
                  prev.map((item) => {
                    if (item.id !== rowId) return item;

                    // Update the field normally
                    const updated = fullRow
                      ? { ...item, ...fullRow }
                      : { ...item, [key]: value };

                    // Payment must remain untouched unless user edits payment
                    if (key !== "payment") {
                      updated.payment = item.payment; // keep original API payment
                    }

                    const hasProduct = !!(updated.product || updated.product_id);
                    const unitPrice = hasProduct
                      ? Number(updated.price ?? updated.product?.final_price ?? 0)
                      : 0;
                    const qty = Number(updated.product_qty || 0);

                    // Pay Amount calculation
                    const amountNeedToPay = hasProduct
                      ? unitPrice * qty
                      : Number(updated.deposit || 0);

                    updated.amountNeedToPay = amountNeedToPay;

                    // Balance can be negative when payment exceeds amount
                    updated.balance = getItemBalance(updated);

                    return updated;
                  }),
                );
              }}
            />

            <div className="row mt-4 g-4">
              <div className="col-md-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-header bg-primary text-white fw-semibold">
                    Page Totals
                  </div>

                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Qty</span>
                      <span className="fw-bold">{totalQty}</span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Payment</span>
                      <span className="fw-bold text-danger">
                        ${totalPayment}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Deposit</span>
                      <span className="fw-bold text-success">
                        ${totalDeposit}
                      </span>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between fs-6">
                      <span className="fw-semibold">Balance</span>
                      <span className="fw-bold text-primary">
                        ${totalBalance}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Grant Totals */}
              <div className="col-md-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-header bg-dark text-white fw-semibold">
                    Grant Totals
                  </div>

                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Qty</span>
                      <span className="fw-bold">{total?.total_qty}</span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Payment</span>
                      <span className="fw-bold text-danger">
                        ${total?.total_payment}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Deposit</span>
                      <span className="fw-bold text-success">
                        ${total?.total_deposit}
                      </span>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between fs-6">
                      <span className="fw-semibold">Balance</span>
                      <span className="fw-bold text-primary">
                        ${total?.total_balance}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Invoice;
